import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  Card,
  Chip,
  DataTable,
  Kpi,
  Legend,
  PageHead,
  Pager,
  StackedBars,
  StateBlock,
  type Column,
  type IconName,
  type Tone,
} from '@w7/ui'
import { useSession, useTransfers } from '@/app/queries'
import { AccountCell } from '@/app/AppShell'
import { Amount, AsOf, exportCsv } from '@/app/bits'
import { count, dayLabel, EMPTY, isoDay, lll, money, moneyCompact } from '@/lib/format'
import type { Account, Transfer } from '@/data/types'
import {
  FilteredEmpty,
  ReportFilters,
  ScopeBanner,
  ScopeUnavailable,
  scopeSuffix,
  scopedCsvName,
  useReportFilters,
  type Dimension,
} from './ReportFilters'

/**
 * Credit transfers — replaces "viewTransactionNew".
 *
 * The original was a flat list: Creation Date / From user / To user / Amount /
 * Transaction Type / Comments, a VIEW MORE button, and nothing else. No totals,
 * so "how much did I hand out this month" meant exporting and adding it up by
 * hand. No balances, so no row could be reconciled against the ledger. And
 * amounts were coloured — green on one row, red on another — with no rule behind
 * it, which taught operators to ignore colour exactly where it should mean
 * something.
 *
 * Here the period totals sit above the list, every row carries the counterparty's
 * balance after the movement, and amounts are deliberately NEUTRAL: giving credit
 * is not a loss and taking it back is not a win. Green and red stay reserved for
 * settled P&L across this console.
 */

const PAGE_SIZE = 25
const DAY_MS = 86_400_000

/**
 * The one report where a tier filter is real — a credit movement's counterparty
 * can be any tier, and "show me only movements with my Masters" is a genuine
 * Monday question. Sport and game are absent and the panel says why in one
 * line: a credit movement has no sport and no game bucket.
 */
const DIMENSIONS: Dimension[] = ['range', 'scope', 'dir', 'role', 'member', 'q']

type TransferRow = Transfer & { from: Account | null; to: Account | null }

type Segment = 'all' | 'give' | 'take'

/**
 * Which way the credit moved, always from the signed-in account's seat.
 *
 * `Transfer.direction` is stored from the ACTING agent's point of view, so it
 * can never be rendered as-is. The third case is real and the original hid it
 * behind two undifferentiated user columns: movements between two accounts
 * further down the chain, where you are neither party.
 */
type Flow = 'gave' | 'took' | 'downline'

const flowOf = (row: TransferRow, selfId: string | undefined): Flow =>
  row.fromId === selfId ? 'gave' : row.toId === selfId ? 'took' : 'downline'

const FLOW: Record<Flow, { label: string; tone: Tone; icon: IconName }> = {
  gave: { label: 'Gave', tone: 'info', icon: 'give' },
  took: { label: 'Took', tone: 'brand', icon: 'take' },
  downline: { label: 'Downline', tone: 'muted', icon: 'downline' },
}

/** The account the row is *about* from where the reader sits. */
const counterpartyOf = (row: TransferRow, flow: Flow): Account | null =>
  flow === 'took' ? row.from : row.to

/** That same account's ledger balance immediately after the movement. */
const balanceAfterOf = (row: TransferRow, flow: Flow): number =>
  flow === 'took' ? row.balanceAfterFrom : row.balanceAfterTo

/**
 * Compact rupees, guarded below ₹1,000.
 *
 * `moneyCompact` strips trailing zeros off its own output there, so ₹500 renders
 * as "₹5" — and credit movements in a downline are routinely two or three
 * figures. Reported against src/lib/format.ts; until that lands, small figures
 * fall back to the grouped format, which is what the fix will produce anyway.
 */
const compact = (value: number | null | undefined, symbol = true): string =>
  typeof value === 'number' && Math.abs(value) < 1000
    ? money(value, { symbol, round: true })
    : moneyCompact(value, symbol)

/** A signed compact figure — the money formatters show a minus but never a plus. */
const signedCompact = (value: number | null | undefined): string =>
  typeof value === 'number' && value > 0 ? `+${compact(value)}` : compact(value)

export function TransfersPage() {
  const filters = useReportFilters(DIMENSIONS)
  const { values, scopeAccount } = filters
  const range = values.range
  const { data: session } = useSession()
  const navigate = useNavigate()

  const segment: Segment = values.dir ?? 'all'
  const page = values.page
  const scopeArg = values.scope ? { accountId: values.scope } : {}

  /*
   * Two queries on purpose. The tiles and the chart describe the whole period;
   * the toolbar narrows only the list beneath them. On one query, switching to
   * "Taken" made the "Given out" tile read ₹0 — which is not a fact about the
   * period, it is an artefact of the filter.
   *
   * Range and scope go into BOTH, because either genuinely changes the period
   * being described; direction, text, tier and counterparty go into the list
   * alone, because they narrow which of that period's movements you are reading.
   */
  const summary = useTransfers({
    range: { from: range.from, to: range.to },
    ...scopeArg,
    page: 1,
    pageSize: 1,
  })

  const { data, isLoading, dataUpdatedAt } = useTransfers({
    range: { from: range.from, to: range.to },
    ...scopeArg,
    ...(values.dir ? { direction: values.dir } : {}),
    ...(values.roles.length ? { roles: values.roles } : {}),
    ...(values.members.length ? { counterpartyIds: values.members } : {}),
    search: values.q,
    page,
    pageSize: PAGE_SIZE,
  })

  const selfId = session?.account.id
  const rows: TransferRow[] = data?.rows ?? []
  const totals = summary.data?.totals
  const asOf = data?.asOf ?? summary.data?.asOf ?? (dataUpdatedAt || undefined)
  const balanceAfterIsLive = Boolean(data?.balanceAfterIsLive ?? summary.data?.balanceAfterIsLive)

  const flow = useMemo(() => {
    const period = summary.data
    if (!period) return null
    const byDate = new Map(period.daily.map((d) => [d.date, d]))
    // The API returns only days that saw a movement. Walking the range instead
    // keeps the axis gap-free, so two bars side by side really are two
    // consecutive days rather than a fortnight silently collapsed.
    const dates: string[] = []
    for (
      let t = Date.parse(`${range.from}T00:00:00`);
      t <= Date.parse(`${range.to}T00:00:00`);
      t += DAY_MS
    ) {
      dates.push(isoDay(t))
    }
    return {
      categories: dates.map((d) => dayLabel(d)),
      taken: dates.map((d) => byDate.get(d)?.taken ?? 0),
      // Negative so the chart's domain reaches below the zero rule; `diverging`
      // mirrors series by index, and the axis still needs a real lower bound.
      given: dates.map((d) => -(byDate.get(d)?.given ?? 0)),
      giveDays: period.daily.filter((d) => d.given > 0).length,
      takeDays: period.daily.filter((d) => d.taken > 0).length,
    }
  }, [summary.data, range.from, range.to])

  const pageTotal = rows.reduce((sum, row) => sum + row.amount, 0)

  const columns = useMemo<Column<TransferRow>[]>(
    () => [
      {
        id: 'at',
        header: 'When',
        width: 176,
        cell: (row) => <span className="w7-mono">{lll(row.at)}</span>,
        sortValue: (row) => row.at,
      },
      {
        id: 'direction',
        header: 'Direction',
        width: 118,
        cell: (row) => {
          const spec = FLOW[flowOf(row, selfId)]
          return (
            <Chip tone={spec.tone} size="sm" icon={spec.icon}>
              {spec.label}
            </Chip>
          )
        },
        sortValue: (row) => FLOW[flowOf(row, selfId)].label,
      },
      {
        id: 'counterparty',
        header: 'Counterparty',
        help: 'The account on the other side. For a movement between two accounts below you, the one that received the credit.',
        cell: (row) => {
          const kind = flowOf(row, selfId)
          const other = counterpartyOf(row, kind)
          if (!other) return <span className="w7-dim">{EMPTY}</span>
          // Only link where the profile is actually readable by you: your own
          // upline shows up in a transfer row but sits outside your subtree.
          const to = selfId && other.path.includes(selfId) ? `/member/${other.id}` : undefined
          return (
            <AccountCell
              username={other.username}
              displayName={other.displayName}
              role={other.role}
              to={to}
              meta={kind === 'downline' && row.from ? `from ${row.from.username}` : undefined}
            />
          )
        },
        sortValue: (row) => counterpartyOf(row, flowOf(row, selfId))?.username ?? null,
      },
      {
        id: 'amount',
        header: 'Amount',
        numeric: true,
        width: 132,
        help: 'Credit moved. Shown neutral on purpose — a give is not a loss and a take is not a win.',
        cell: (row) => <Amount value={row.amount} />,
        sortValue: (row) => row.amount,
        total: <Amount value={pageTotal} />,
      },
      {
        id: 'balance',
        header: balanceAfterIsLive ? 'Balance now' : 'Balance after',
        numeric: true,
        width: 140,
        hideBelow: 1080,
        help: balanceAfterIsLive
          ? 'Live wallet balance for the downline party (analytics). Not the historical post-movement balance — the mart does not store that.'
          : 'The counterparty’s ledger balance immediately after this movement — the reconciliation the old list never showed.',
        cell: (row) => <Amount value={balanceAfterOf(row, flowOf(row, selfId))} />,
        sortValue: (row) => balanceAfterOf(row, flowOf(row, selfId)),
      },
      {
        id: 'remark',
        header: 'Remark',
        hideBelow: 1280,
        cell: (row) => <span className="w7-truncate">{row.remark || EMPTY}</span>,
        sortValue: (row) => row.remark,
      },
      {
        id: 'ref',
        header: 'Ref',
        width: 108,
        hideBelow: 900,
        help: 'Quote this reference when raising the movement with support.',
        cell: (row) => <span className="w7-mono w7-dim">{row.ref}</span>,
        sortValue: (row) => row.ref,
      },
    ],
    [selfId, pageTotal, balanceAfterIsLive],
  )


  const download = () => {
    exportCsv(
      scopedCsvName('w7-credit-transfers', scopeAccount, range),
      rows.map((row) => {
        const kind = flowOf(row, selfId)
        return {
          When: lll(row.at),
          Direction: FLOW[kind].label,
          Counterparty: counterpartyOf(row, kind)?.username ?? '',
          // Raw numbers, not formatted rupees — a spreadsheet has to be able to
          // total this column without a find-and-replace first.
          Amount: row.amount,
          [balanceAfterIsLive ? 'Balance now' : 'Balance after']: balanceAfterOf(row, kind),
          Remark: row.remark,
          Ref: row.ref,
        }
      }),
    )
  }

  const EMPTY_COPY: Record<Segment, { title: string; body: string }> = {
    all: {
      title: 'No credit moved in this period',
      body: 'Gives and takes with your direct downline appear here, alongside movements between the agents beneath you.',
    },
    give: {
      title: 'You have not given credit in this period',
      body: 'Credit you hand down to your direct downline appears here, with the balance each account was left holding.',
    },
    take: {
      title: 'You have not taken credit back in this period',
      body: 'Credit you pull back from your direct downline appears here, with the balance each account was left holding.',
    },
  }
  const empty = EMPTY_COPY[segment]

  /** Direction is the only active filter — its own copy still applies. */
  const onlyDirectionFiltered =
    !values.rangePinned &&
    values.scope === null &&
    values.roles.length === 0 &&
    values.members.length === 0 &&
    values.q.trim().length === 0

  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))

  const head = (
    <PageHead
      title="Credit transfers"
      subtitle={`Every give and take in your downline · ${range.label}${scopeSuffix(scopeAccount)}`}
    />
  )

  if (filters.scopeInvalid) {
    return (
      <>
        {head}
        <ScopeUnavailable onClear={() => filters.clearDimension('scope')} />
      </>
    )
  }

  return (
    <>
      {head}

      <ReportFilters
        state={filters}
        memberSource="downline"
        memberLabel="Counterparties"
        memberAllLabel="All counterparties"
        roleLabel="Counterparty tier"
        textLabel="Search transfers by username, reference or remark"
        textPlaceholder="Username, ref or remark"
        omission="A credit movement has no sport and no game bucket, so neither is offered. Direction, tier, counterparty and text narrow the list only — the tiles and the chart describe the whole period, which is why switching to Taken does not make Given out read ₹0."
        actions={
          // This screen does not poll — `AsOf` says so by omission, because a
          // transfer ledger that claimed to refresh itself would be a lie.
          <>
            <AsOf at={asOf} />

            <Button icon="download" variant="secondary" onClick={download} disabled={rows.length === 0}>
              Export CSV
            </Button>
          </>
        }
      />

      {scopeAccount ? (
        <ScopeBanner account={scopeAccount} onClear={() => filters.clearDimension('scope')} />
      ) : null}

      {/* ---------------------------------------------------------- KPIs -- */}
      <div className="w7-grid w7-grid-4" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Given out"
          value={totals ? compact(totals.given) : EMPTY}
          exact={money(totals?.given)}
          meta={
            flow ? `Handed down by you on ${count(flow.giveDays)} of ${count(range.days)} days` : undefined
          }
        />
        <Kpi
          label="Taken in"
          value={totals ? compact(totals.taken) : EMPTY}
          exact={money(totals?.taken)}
          meta={
            flow ? `Pulled back by you on ${count(flow.takeDays)} of ${count(range.days)} days` : undefined
          }
        />
        {/* Signed, but never green or red. Net credit movement is not a result —
            colouring it would be the exact defect this screen exists to fix. */}
        <Kpi
          label="Net movement"
          value={totals ? signedCompact(totals.net) : EMPTY}
          exact={money(totals?.net, { signed: true })}
          meta={
            totals
              ? totals.net >= 0
                ? 'Taken less given — more came back up to you than went down'
                : 'Taken less given — more went down the chain than came back'
              : undefined
          }
        />
        <Kpi
          label="Movements"
          value={totals ? count(totals.count) : EMPTY}
          meta="Every give and take in your downline, not only the ones you were party to"
        />
      </div>

      {/* --------------------------------------------------------- chart -- */}
      <Card
        title="Credit in and out by day"
        subtitle="Above the line is credit you took back; below the line is credit you gave out"
        style={{ marginBottom: 'var(--w7-s5)' }}
      >
        {flow ? (
          <>
            <StackedBars
              label="Credit taken in and given out by day"
              categories={flow.categories}
              height={220}
              diverging
              maxLabels={8}
              format={(v) => compact(v, false)}
              series={[
                { id: 'taken', label: 'Taken in', values: flow.taken, color: 'var(--w7-c2)' },
                { id: 'given', label: 'Given out', values: flow.given, color: 'var(--w7-c1)' },
              ]}
              emptyMessage="No credit moved in this period"
            />
            <Legend
              items={[
                { label: 'Taken in', color: 'var(--w7-c2)', value: compact(totals?.taken) },
                { label: 'Given out', color: 'var(--w7-c1)', value: compact(totals?.given) },
              ]}
            />
          </>
        ) : (
          <div style={{ height: 220 }} />
        )}
      </Card>

      {/* --------------------------------------------------------- table -- */}
      <Card
        flush
        title="Movements"
        subtitle={isLoading ? 'Loading' : `${count(data?.total ?? 0)} matching this filter`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={filters.setPage}
            total={data?.total ?? 0}
            pageSize={PAGE_SIZE}
          />
        }
      >
        <DataTable
          label="Credit transfers"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading || !session}
          skeletonRows={8}
          showTotals={rows.length > 0}
          totalsLabel="This page"
          empty={
            /* The direction segment has its own copy because "you have not
               given credit" is a fact about you, not about a filter. Anything
               else narrowing the list gets the generic filtered-empty state,
               which names the culprit and offers Clear all. */
            filters.isFiltered && !(segment !== 'all' && onlyDirectionFiltered) ? (
              <FilteredEmpty state={filters} icon="transfers" />
            ) : (
              <StateBlock
                icon="transfers"
                title={empty.title}
                actions={
                  <Button variant="secondary" icon="give" onClick={() => navigate('/downline')}>
                    Give or take credit
                  </Button>
                }
              >
                {empty.body}
              </StateBlock>
            )
          }
        />
      </Card>
    </>
  )
}
